// Maya Premium paywall (prices always read live from Stripe) — shows a comparison card (single destination vs.
// Maya Pass México) and opens an embedded Stripe checkout for the chosen
// product. On success, the surrounding access query is invalidated so the
// audio guide unlocks automatically.
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/lib/lang";
import { useCurrency } from "@/lib/currency";
import { chargeDisclosure } from "@/lib/booking-display";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";

import { MAYA_IMAGE_URL as mayaIcon } from "@/lib/maya-art";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Crown, Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getStripe,
  getStripeEnvironment,
  isStripeConfigured,
} from "@/lib/stripe-browser";
import {
  MAYA_PRODUCTS,
  createMayaCheckoutSession,
  getMayaOffer,
  type MayaOfferPrice,
} from "@/lib/maya-checkout.functions";
import { rememberMayaEmail, rememberMayaReturnPath } from "@/lib/maya-guest";

interface Props {
  destinationKey: string;
  /** When set, checkout unlocks this specific tour instead of the whole destination. */
  tourSlug?: string | null;
  customerEmail: string | null;
  userId: string | null;
  /** Labels in the active language. */
  lang: "es" | "en" | "fr" | "pt" | "it" | "de";
}


type L = {
  locked: string; intro: string; single: string; singleSub: string; tourSub: string;
  pass: string; passSub: string; bestValue: string; unlock: string; upgrade: string;
  needEmail: string; emailPlaceholder: string; continue: string; bullets: string[];
  passBullets: (n: number) => string[]; notConfigured: string; approx: string;
  errGeneric: string; errAttempts: string; errDeclines: string; errPrice: string;
};

const LABELS: Record<"es" | "en" | "fr" | "pt" | "it" | "de", L> = {
  es: {
    locked: "Esta es una guía Maya Premium",
    intro: "Desbloquea la narración completa: historia, cultura, gastronomía, flora, fauna, leyendas y consejos de viaje.",
    single: "Maya Premium", singleSub: "Guía de este destino", tourSub: "Guía de este tour",
    pass: "Maya Pass México", passSub: "Acceso a todas las guías disponibles",
    bestValue: "⭐ Mejor valor", unlock: "Desbloquear", upgrade: "Obtener Pass",
    needEmail: "Inicia sesión o agrega tu correo para continuar", emailPlaceholder: "tu@email.com", continue: "Continuar",
    bullets: ["Historia y arqueología", "Cultura y tradiciones", "Gastronomía local", "Flora, fauna y geografía"],
    passBullets: (n) => [`Todas las guías disponibles (${n} hoy) y las que agreguemos`, "Pago único, sin fecha de vencimiento"],
    notConfigured: "El pago no está configurado aún. Vuelve pronto para desbloquear a Maya Premium.",
    approx: "aprox.",
    errGeneric: "No pudimos abrir el pago. Inténtalo de nuevo en unos minutos.",
    errAttempts: "Demasiados intentos. Espera unos minutos antes de volver a intentarlo.",
    errDeclines: "Por seguridad bloqueamos nuevos pagos con este correo o conexión durante 24 h tras varios rechazos de tarjeta.",
    errPrice: "Este producto no está disponible en este momento.",
  },
  en: {
    locked: "This is a Maya Premium guide",
    intro: "Unlock the full narration: history, culture, food, flora, fauna, legends and travel tips.",
    single: "Maya Premium", singleSub: "Guide to this destination", tourSub: "Guide to this tour",
    pass: "Maya Pass México", passSub: "Access to every available guide",
    bestValue: "⭐ Best value", unlock: "Unlock", upgrade: "Get Pass",
    needEmail: "Sign in or enter your email to continue", emailPlaceholder: "you@email.com", continue: "Continue",
    bullets: ["History & archaeology", "Culture & traditions", "Local gastronomy", "Flora, fauna & geography"],
    passBullets: (n) => [`Every available guide (${n} today) and the ones we add`, "One-time payment, no expiry date"],
    notConfigured: "Payments are not configured yet. Maya Premium will unlock once setup is complete.",
    approx: "approx.",
    errGeneric: "We couldn't open the payment. Please try again in a few minutes.",
    errAttempts: "Too many attempts. Please wait a few minutes and try again.",
    errDeclines: "For security, new payments from this email or connection are blocked for 24 h after several card declines.",
    errPrice: "This product isn't available right now.",
  },
  fr: {
    locked: "Ceci est un guide Maya Premium",
    intro: "Débloque la narration complète : histoire, culture, gastronomie, faune, flore, légendes et conseils.",
    single: "Maya Premium", singleSub: "Guide de cette destination", tourSub: "Guide de cette excursion",
    pass: "Maya Pass México", passSub: "Accès à tous les guides disponibles",
    bestValue: "⭐ Meilleure offre", unlock: "Débloquer", upgrade: "Obtenir le Pass",
    needEmail: "Connecte-toi ou ajoute ton email pour continuer", emailPlaceholder: "ton@email.com", continue: "Continuer",
    bullets: ["Histoire et archéologie", "Culture et traditions", "Gastronomie locale", "Flore, faune et géographie"],
    passBullets: (n) => [`Tous les guides disponibles (${n} aujourd'hui) et ceux que nous ajouterons`, "Paiement unique, sans date d'expiration"],
    notConfigured: "Le paiement n'est pas encore configuré. Maya Premium sera bientôt disponible.",
    approx: "env.",
    errGeneric: "Impossible d'ouvrir le paiement. Réessaie dans quelques minutes.",
    errAttempts: "Trop de tentatives. Attends quelques minutes avant de réessayer.",
    errDeclines: "Par sécurité, les nouveaux paiements avec cet email ou cette connexion sont bloqués 24 h après plusieurs refus de carte.",
    errPrice: "Ce produit n'est pas disponible pour le moment.",
  },
  pt: {
    locked: "Este é um guia Maya Premium",
    intro: "Desbloqueie a narração completa: história, cultura, gastronomia, flora, fauna, lendas e dicas.",
    single: "Maya Premium", singleSub: "Guia deste destino", tourSub: "Guia deste passeio",
    pass: "Maya Pass México", passSub: "Acesso a todos os guias disponíveis",
    bestValue: "⭐ Melhor valor", unlock: "Desbloquear", upgrade: "Obter Pass",
    needEmail: "Entre ou informe seu email para continuar", emailPlaceholder: "voce@email.com", continue: "Continuar",
    bullets: ["História e arqueologia", "Cultura e tradições", "Gastronomia local", "Flora, fauna e geografia"],
    passBullets: (n) => [`Todos os guias disponíveis (${n} hoje) e os que adicionarmos`, "Pagamento único, sem data de vencimento"],
    notConfigured: "Pagamentos ainda não configurados. Maya Premium estará disponível em breve.",
    approx: "aprox.",
    errGeneric: "Não conseguimos abrir o pagamento. Tente novamente em alguns minutos.",
    errAttempts: "Muitas tentativas. Aguarde alguns minutos e tente novamente.",
    errDeclines: "Por segurança, novos pagamentos com este email ou conexão ficam bloqueados por 24 h após várias recusas de cartão.",
    errPrice: "Este produto não está disponível no momento.",
  },
  it: {
    locked: "Questa è una guida Maya Premium",
    intro: "Sblocca la narrazione completa: storia, cultura, gastronomia, flora, fauna, leggende e consigli di viaggio.",
    single: "Maya Premium", singleSub: "Guida di questa destinazione", tourSub: "Guida di questo tour",
    pass: "Maya Pass México", passSub: "Accesso a tutte le guide disponibili",
    bestValue: "⭐ Miglior valore", unlock: "Sblocca", upgrade: "Ottieni il Pass",
    needEmail: "Accedi o inserisci la tua email per continuare", emailPlaceholder: "tu@email.com", continue: "Continua",
    bullets: ["Storia e archeologia", "Cultura e tradizioni", "Gastronomia locale", "Flora, fauna e geografia"],
    passBullets: (n) => [`Tutte le guide disponibili (${n} oggi) e quelle che aggiungeremo`, "Pagamento unico, senza scadenza"],
    notConfigured: "I pagamenti non sono ancora configurati. Maya Premium sarà presto disponibile.",
    approx: "circa",
    errGeneric: "Non siamo riusciti ad aprire il pagamento. Riprova tra qualche minuto.",
    errAttempts: "Troppi tentativi. Attendi qualche minuto e riprova.",
    errDeclines: "Per sicurezza, i nuovi pagamenti con questa email o connessione sono bloccati per 24 h dopo vari rifiuti della carta.",
    errPrice: "Questo prodotto non è disponibile al momento.",
  },
  de: {
    locked: "Dies ist ein Maya-Premium-Guide",
    intro: "Schalte die komplette Erzählung frei: Geschichte, Kultur, Küche, Flora, Fauna, Legenden und Reisetipps.",
    single: "Maya Premium", singleSub: "Guide zu diesem Reiseziel", tourSub: "Guide zu dieser Tour",
    pass: "Maya Pass México", passSub: "Zugang zu allen verfügbaren Guides",
    bestValue: "⭐ Bestes Angebot", unlock: "Freischalten", upgrade: "Pass holen",
    needEmail: "Melde dich an oder gib deine E-Mail ein, um fortzufahren", emailPlaceholder: "du@email.com", continue: "Weiter",
    bullets: ["Geschichte & Archäologie", "Kultur & Traditionen", "Lokale Küche", "Flora, Fauna & Geografie"],
    passBullets: (n) => [`Alle verfügbaren Guides (heute ${n}) und alle, die wir hinzufügen`, "Einmalzahlung, ohne Ablaufdatum"],
    notConfigured: "Zahlungen sind noch nicht eingerichtet. Maya Premium ist bald verfügbar.",
    approx: "ca.",
    errGeneric: "Die Zahlung konnte nicht geöffnet werden. Bitte versuche es in ein paar Minuten erneut.",
    errAttempts: "Zu viele Versuche. Bitte warte ein paar Minuten.",
    errDeclines: "Aus Sicherheitsgründen sind neue Zahlungen mit dieser E-Mail oder Verbindung nach mehreren Kartenablehnungen 24 Std. gesperrt.",
    errPrice: "Dieses Produkt ist derzeit nicht verfügbar.",
  },
};

function fmt(p: MayaOfferPrice | undefined, lang: string): string | null {
  if (!p) return null;
  return new Intl.NumberFormat(lang, { style: "currency", currency: p.currency, minimumFractionDigits: 2 }).format(p.amountMinor / 100);
}

function fmtMxn(p: MayaOfferPrice | undefined, approx: string): string | null {
  if (!p?.approxMxn) return null;
  return `${approx} $${p.approxMxn.toLocaleString("es-MX")} MXN`;
}

export function MayaPaywall({
  destinationKey,
  tourSlug = null,
  customerEmail,
  userId,
  lang: initialLang,
}: Props) {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language || initialLang);
  const { currency, format, rates } = useCurrency();
  const fmt = (p: MayaOfferPrice | undefined, locale: string) => p ? format(p.amountMinor / 100, p.currency, {locale, decimals:2}) : null;
  const t = LABELS[lang];
  const qc = useQueryClient();
  const configured = isStripeConfigured();

  const [emailDraft, setEmailDraft] = useState(customerEmail ?? "");
  const [chosen, setChosen] = useState<"destination" | "pass" | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: offer } = useQuery({
    queryKey: ["maya-offer", configured ? getStripeEnvironment() : "off"],
    queryFn: () => getMayaOffer({ data: { environment: getStripeEnvironment() } }),
    enabled: configured,
    staleTime: 10 * 60_000,
  });
  const singleKey = tourSlug ? MAYA_PRODUCTS.tour : MAYA_PRODUCTS.destination;
  const singlePrice = offer?.prices[singleKey];
  const passPrice = offer?.prices[MAYA_PRODUCTS.pass];
  const guidesCount = offer?.guidesCount ?? 0;
  const showPass = guidesCount >= 5 && !!passPrice;
  const errText = (code: string) =>
    code === "ERR_ATTEMPTS" ? t.errAttempts : code === "ERR_DECLINES" ? t.errDeclines : code === "ERR_PRICE" ? t.errPrice : t.errGeneric;

  const effectiveEmail = (customerEmail || emailDraft).trim();
  const emailValid = /.+@.+\..+/.test(effectiveEmail);

  async function startCheckout(which: "destination" | "pass") {
    if (!configured) {
      setError(t.notConfigured);
      return;
    }
    if (!emailValid) {
      setChosen(which);
      return;
    }
    setLoading(true);
    setError(null);
    setChosen(which);
    rememberMayaEmail(effectiveEmail);
    if (typeof window !== "undefined") {
      rememberMayaReturnPath(window.location.pathname + window.location.search);
    }
    try {
      // When a tourSlug is provided we buy per-tour access (new model);
      // without it we fall back to the legacy per-destination product so
      // Maya Hub pages keep working.
      const singleProduct =
        which === "destination"
          ? tourSlug
            ? MAYA_PRODUCTS.tour
            : MAYA_PRODUCTS.destination
          : MAYA_PRODUCTS.pass;
      const res = await createMayaCheckoutSession({
        data: {
          product: singleProduct,
          destinationKey,
          ...(tourSlug ? { tourSlug } : {}),
          customerEmail: effectiveEmail,
          userId: userId ?? undefined,
          returnUrl: `${window.location.origin}/maya/return?session_id={CHECKOUT_SESSION_ID}`,
          environment: getStripeEnvironment(),
          lang,
        },
      });
      if ("error" in res) throw new Error(errText(res.error));
      if (!res.clientSecret) throw new Error(t.errGeneric);
      setClientSecret(res.clientSecret);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t.errGeneric);
      setChosen(null);
    } finally {
      setLoading(false);
    }
  }

  function closeCheckout() {
    setClientSecret(null);
    setChosen(null);
    // refresh access in case the user paid in another tab
    qc.invalidateQueries({ queryKey: ["maya-access"] });
  }

  const checkoutOptions = useMemo(
    () => (clientSecret ? { fetchClientSecret: async () => clientSecret } : null),
    [clientSecret],
  );

  return (
    <div className="relative mt-5 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-amber-500/5 p-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white ring-1 ring-primary/20">
          <img loading="lazy" decoding="async" src={mayaIcon} alt="Maya" className="h-9 w-9 rounded-lg object-contain" />
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            <Lock className="h-3.5 w-3.5" /> {t.locked}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t.intro}</p>
        </div>
      </div>

      <div className={cn("mt-4 grid gap-3", showPass && "sm:grid-cols-2")}>
        {/* Single destination */}
        <div className="relative rounded-xl border border-border bg-background p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">🌺 {t.single}</h3>
          </div>
          <p className="text-xs text-muted-foreground">{tourSlug ? t.tourSub : t.singleSub}</p>
          {singlePrice && (
            <div className="mt-3 text-2xl font-bold">
              {fmt(singlePrice, lang)} <span className="text-xs font-normal text-muted-foreground">{currency}</span>
              <div className="mt-1 text-xs font-normal text-muted-foreground">{chargeDisclosure(singlePrice.amountMinor / 100, singlePrice.currency, currency, lang, rates)}</div>
            </div>
          )}
          <ul className="mt-3 space-y-1 text-xs">
            {t.bullets.slice(0, 4).map((b) => (
              <li key={b} className="flex items-start gap-1.5">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {b}
              </li>
            ))}
          </ul>
          <Button
            onClick={() => startCheckout("destination")}
            disabled={loading || !singlePrice}
            variant="outline"
            className="mt-4 w-full"
            size="sm"
          >
            {t.unlock}{singlePrice ? ` · ${fmt(singlePrice, lang)}` : ""}
          </Button>
        </div>

        {/* Pass — solo con 5 o más guías publicadas */}
        {showPass && <div className="relative rounded-xl border-2 border-primary bg-background p-4 shadow-md">
          <span className="absolute -top-2.5 left-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
            {t.bestValue}
          </span>
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-semibold">👑 {t.pass}</h3>
          </div>
          <p className="text-xs text-muted-foreground">{t.passSub}</p>
          <div className="mt-3 text-2xl font-bold">
            {fmt(passPrice, lang)} <span className="text-xs font-normal text-muted-foreground">{currency}</span>
            {passPrice && <div className="mt-1 text-xs font-normal text-muted-foreground">{chargeDisclosure(passPrice.amountMinor / 100, passPrice.currency, currency, lang, rates)}</div>}
          </div>
          <ul className="mt-3 space-y-1 text-xs">
            {t.passBullets(guidesCount).map((b) => (
              <li key={b} className="flex items-start gap-1.5">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" /> {b}
              </li>
            ))}
          </ul>
          <Button
            onClick={() => startCheckout("pass")}
            disabled={loading}
            className="mt-4 w-full"
            size="sm"
          >
            {t.upgrade} · {fmt(passPrice, lang)}
          </Button>
        </div>}
      </div>

      {chosen && !emailValid && (
        <div className="mt-4 rounded-lg border border-border bg-background p-3">
          <label className="text-xs font-semibold">{t.needEmail}</label>
          <div className="mt-2 flex gap-2">
            <input
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder={t.emailPlaceholder}
              className={cn(
                "flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-primary/30",
              )}
            />
            <Button
              size="sm"
              onClick={() => startCheckout(chosen)}
              disabled={!/.+@.+\..+/.test(emailDraft.trim())}
            >
              {t.continue}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
          {error}
        </p>
      )}

      <Dialog open={!!clientSecret} onOpenChange={(o) => !o && closeCheckout()}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>🌺 Maya Premium</DialogTitle>
          </DialogHeader>
          <div className="px-2 pb-4 sm:px-4">
            {checkoutOptions && (
              <EmbeddedCheckoutProvider stripe={getStripe()} options={checkoutOptions}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
